// SynchroService.ts — imports : remplacer BaseApiService/TenantService par AbstractService/AppService/env/ApiResponse
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, interval, switchMap, takeWhile, catchError, map } from 'rxjs';
import { AbstractService } from '../../../core/AbstractService';
import { AppService } from '../../../core/app.service';
import { ApiResponse } from '../../../data/http/responses/apiresponse.response';
import { env } from '../../../../env/env';
import { ExportBddVersKcRequest, SynchroJobStatut } from './../../../pages/app/model/keyclaok/adminservice/keycloak-admin.model';
/**
 * Service Angular — miroir de SynchroKeycloakController
 * Base URL : /v0.1/api/simo/keycloak-admin/synchro
 *
 * Deux approches disponibles :
 *
 *  1. Fire-and-forget  → lancer*() retourne les infos du job lancé.
 *                        Interroger getStatut(jobId) manuellement.
 *
 *  2. Polling auto     → lancer*AvecSuivi() retourne Observable<SynchroJobStatut>
 *                        qui émet à chaque tick et se complète quand le job
 *                        passe à TERMINE ou ERREUR_FATALE.
 */
@Injectable({ providedIn: 'root' })
export class SynchroService extends AbstractService<any, any> {

  private readonly BASE = 'keycloak-admin/synchro';

  constructor(http: HttpClient, appService: AppService) {
    super(`${env.apiUrl}`, http, null, appService);
  }

  // Helpers HTTP génériques — remplacent ceux hérités de BaseApiService.
  // Préfixés 'http' pour éviter tout conflit de nom avec les méthodes publiques de AbstractService (ex. delete).
  private httpGet<T>(path: string, options?: { params?: HttpParams }): Observable<T> {
    return this.http.get<ApiResponse<T>>(`${this.baseUrl}${path}`, { headers: this.getHeaders(), ...options })
      .pipe(map(res => res.data as T), catchError(this.handleError));
  }

  private httpPost<T>(path: string, body: any, options?: { params?: HttpParams }): Observable<T> {
    return this.http.post<ApiResponse<T>>(`${this.baseUrl}${path}`, body, { headers: this.getHeaders(), ...options })
      .pipe(map(res => res.data as T), catchError(this.handleError));
  }

  // ==========================================================================
  // SUIVI
  // ==========================================================================

  getEtatSynchro(): Observable<{ synchronise: boolean }> {
    return this.httpGet<{ synchronise: boolean }>(`${this.BASE}/etat`);
  }

  /**
   * Retourne le statut instantané d'un job.
   * GET /synchro/statut/{jobId}
   */
  getStatut(jobId: string): Observable<SynchroJobStatut> {
    return this.httpGet<SynchroJobStatut>(`${this.BASE}/statut/${jobId}`);
  }

  /**
   * Liste tous les jobs depuis le démarrage de la JVM.
   * GET /synchro/jobs
   */
  listerJobs(): Observable<SynchroJobStatut[]> {
    return this.httpGet<SynchroJobStatut[]>(`${this.BASE}/jobs`);
  }

  // ==========================================================================
  // POLLING AUTO
  // ==========================================================================

  /**
   * Lance l'import KC → BDD puis poll automatiquement le statut.
   *
   * L'Observable émet à chaque tick (toutes les `intervalMs` ms) et
   * se complète automatiquement quand statut ≠ EN_COURS.
   *
   * Usage :
   * ```ts
   * this.synchroService.lancerImportKcVersBddAvecSuivi().subscribe({
   *   next: s  => console.log(`${s.succes} ok / ${s.echecs} erreurs`),
   *   complete: () => console.log('Import terminé'),
   *   error:    e  => console.error(e),
   * });
   * ```
   */
  lancerImportKcVersBddAvecSuivi(intervalMs = 2000, applicationId?: string): Observable<SynchroJobStatut> {
    return new Observable(observer => {
      this.lancerImportKcVersBdd(applicationId).subscribe({   // ← MODIFIÉ
        next: res => this.poll(res['jobId'], intervalMs).subscribe(observer),
        error: err => observer.error(err),
      });
    });
  }

  // SynchroService.ts — à réinsérer avant la section SUIVI (juste après httpPost)
  /**
   * Lance l'import KC → BDD (async).
   * POST /synchro/kc-vers-bdd
   * @returns { jobId, suivi }
   */
  lancerImportKcVersBdd(applicationId?: string): Observable<Record<string, string>> {
    return this.httpPost<Record<string, string>>(
      `${this.BASE}/kc-vers-bdd`,
      applicationId ? { applicationId } : null
    );
  }

  /**
   * Lance l'export BDD → KC (async).
   * POST /synchro/bdd-vers-kc
   * @returns { jobId, suivi }
   */
  lancerExportBddVersKc(
    request: ExportBddVersKcRequest,
  ): Observable<Record<string, string>> {
    return this.httpPost<Record<string, string>>(`${this.BASE}/bdd-vers-kc`, request);
  }


  /**
   * Lance l'export BDD → KC puis poll automatiquement le statut.
   * Même comportement que lancerImportKcVersBddAvecSuivi.
   */
  lancerExportBddVersKcAvecSuivi(
    request: ExportBddVersKcRequest,
    intervalMs = 2000,
  ): Observable<SynchroJobStatut> {
    // pas de changement ici, applicationId est déjà dans request
    return new Observable(observer => {
      this.lancerExportBddVersKc(request).subscribe({
        next: res => this.poll(res['jobId'], intervalMs).subscribe(observer),
        error: err => observer.error(err),
      });
    });
  }

  // ── helper polling ─────────────────────────────────────────────────────────

  private poll(jobId: string, intervalMs: number): Observable<SynchroJobStatut> {
    return interval(intervalMs).pipe(
      switchMap(() => this.getStatut(jobId)),
      // émet le dernier statut terminal PUIS complete (inclusive = true)
      takeWhile(s => s.statut === 'EN_COURS', true),
    );
  }
}


export type { SynchroJobStatut } from '../../../pages/app/model/keyclaok/adminservice/keycloak-admin.model';